import { ISensorInterface } from './ISensorInterface';
import { TranslationService } from '../services/translation.service';
import { SpecialSensorValue } from './SpecialSensorValue';

export class BaseSensor implements ISensorInterface {
	name: string;
	unit: string;

	maxValue: number;
	minValue: number;
	currentValue: number;
	granularity: number;
	specialValues: Map<number, SpecialSensorValue>;

	hooks: Map<string, CallableFunction>;
	type: 'list' | 'numeric' = 'numeric';
	list: Array<string>;


	setList(list: Array<string>): BaseSensor {
		this.list = list;
		this.type = 'list';
		this.minValue = 0;
		this.maxValue = this.list.length - 1;
		this.granularity = 1;
		return this;
	}

	isList(): boolean {
		return this.type == 'list';
	}

	getValueAt(_idx: number): string {
		if (this.type != 'list') {
			throw new String("illegal call: getValueAt on non-list-sensor.");
		}
		let idx = _idx % this.list.length;
		if (idx < 0) {
			idx = this.list.length - idx;
		}
		return TranslationService.getInstance().tr(this.list[idx]);
	}


	setName(_name: string) {
		this.name = _name;
	}
	getName(): string {
		return this.name;
	}
	registerHook(name: string, fn: CallableFunction) {
		this.hooks.set(name, fn);
	}

	public getUnit(): string {
		if (this.specialValues.has(this.currentValue)) {
			return '';
		}
		return TranslationService.getInstance().tr(this.unit);
	}
	public getUnitFor(idx): string {
		let v = Number(this.list[idx]);
		if (isNaN(v)) {
			return '';
		}
		return this.getUnit();

	}

	setUnit(_unit: string): BaseSensor {
		this.unit = _unit;
		return this;
	}
	getMinValue(): number {
		return this.minValue;
	}
	setMinValue(val: number): void {
		if (!this.maxValue || val < this.maxValue) {
			this.minValue = val;
		} else {
			console.log("error setting min-value on sensor " + this.name + ": max-value is set and smaller");
		}
	}

	getMaxValue(): number {
		return this.maxValue;
	}
	setMaxValue(val: number): void {
		if (!this.minValue || val > this.minValue) {
			this.maxValue = val;
		} else {
			console.log("error setting max-value on sensor " + this.name + ": min-value is set and smaller");
		}
	}

	public setCurrentValue(val: number, ignoreHook?: boolean): void {

		this.currentValue = val;
		if (this.currentValue > this.maxValue) {
			this.currentValue = this.maxValue;
		}
		if (this.currentValue < this.minValue) {
			this.currentValue = this.minValue;
		}

		if (!ignoreHook) {
			if (this.hooks.has('valueChanged')) {
				this.hooks.get('valueChanged')(this.currentValue);
			}
		}
	}
	public getCurrentElement(): string {
		return this.list[this.currentValue];
	}
	public getCurrentValue(): number {

		return this.currentValue;
	}

	getGranularity(): number {
		return this.granularity;
	}


	setGranularity(gran: number) {
		this.granularity = gran;
	}

	_savedValue: any = undefined;
	saveValue() {
		this._savedValue = this.currentValue;
	}
	restoreValue() {
		if (this._savedValue) {
			this.setCurrentValue(this._savedValue);
		}
		this._savedValue = undefined;
	}
	toString() {

		if (this.isList()) {
			return TranslationService.getInstance().tr(this.list[this.currentValue]) + " " + TranslationService.getInstance().tr(this.getUnitFor(this.currentValue));
		}
		if (this.specialValues.has(this.currentValue)) {
			console.log("ToString in BaseSensor returns replaced value " + this.specialValues.get(this.currentValue) + " instead " + this.currentValue);
			return TranslationService.getInstance().tr(this.specialValues.get(this.currentValue).getReplacement());
		}
		switch (this.granularity) {
			case 0.1: {
				return this.currentValue.toFixed(1) + " " + this.getUnit();
			}
			case 0.5: {
				return this.currentValue.toFixed(1) + " " + this.getUnit();
			}
			case 0.05: {
				return this.currentValue.toFixed(2) + " " + this.getUnit();
			}
			default: {
				return this.currentValue + " " + this.getUnit();
			}

		}
	}

	static fromArgs(name: string, currentValue: number, min: number, max: number, granularity: number, unit: string): BaseSensor {
		let s = new BaseSensor();
		s.setName(name);
		s.setMinValue(min);
		s.setMaxValue(max);
		s.setCurrentValue(currentValue);
		s.setGranularity(granularity);
		s.setUnit(unit);
		return s;
	}


	public setSpecialValue(value: number, valueReplacement: string, icon?: string) {

		this.specialValues.set(value, new SpecialSensorValue(valueReplacement, icon));
	}
	public hasSpecialValue(key: number): boolean {
		return this.specialValues.has(key);
	}
	public getSpecialValue(key: number) {
		return this.specialValues.get(key);
	}

	increase(wrap?: boolean) {
		let v = this.currentValue;

		v += this.granularity;
		if (v > this.maxValue) {
			if (!wrap) {
				v = this.maxValue;
			} else {
				v = this.minValue;
			}
		}
		this.currentValue = v;
	}
	decrease(wrap?: boolean) {
		let v = this.currentValue;

		v -= this.granularity;
		if (v < this.minValue) {
			if (!wrap) {
				v = this.minValue;
			} else {
				v = this.maxValue;
			}
		}
		this.currentValue = v;
	}

	constructor(sensor?: BaseSensor) {
		this.hooks = new Map<string, CallableFunction>();
		this.specialValues = new Map<number, SpecialSensorValue>();
		if (sensor) {
			this.setName(sensor.getName());
			this.setMinValue(sensor.getMinValue());
			this.setMaxValue(sensor.getMaxValue());
			this.setCurrentValue(sensor.getCurrentValue());
			this.setUnit(sensor.getUnit());
			this.setGranularity(sensor.getGranularity());
			this.specialValues = new Map<number, SpecialSensorValue>(sensor.specialValues);
		} else {
			this.minValue = -999999999;
			this.currentValue = 0;
			this.maxValue = 999999999;
			this.name = 'unknown';
			this.granularity = 0.1;
			this.unit = 'N/A';
		}
		return this;
	}


}